import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, FileText, Sparkles, FolderOpen, Bell, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

interface SearchResult {
  title: string;
  type: string;
  detail: string;
  icon: string;
  expiresAt?: string;
}

interface DemoQuery {
  query: string;
  result: SearchResult;
}

interface MultiDocResult {
  documents: { title: string; type: string; icon: string }[];
  collectionName: string;
}

interface DemoQueryMulti {
  query: string;
  isMultiDoc: true;
  result: MultiDocResult;
}

interface DemoQuerySingle {
  query: string;
  isMultiDoc?: false;
  result: SearchResult;
}

type DemoQueryType = DemoQuerySingle | DemoQueryMulti;

const demoQueries: DemoQueryType[] = [
  {
    query: "Ma facture EDF de septembre",
    result: {
      title: "Facture EDF",
      type: "Énergie",
      detail: "Sept. 2024 — 127,50€",
      icon: "⚡"
    }
  },
  {
    query: "Dossier prêt immobilier",
    isMultiDoc: true,
    result: {
      collectionName: "Dossier Prêt 2024",
      documents: [
        { title: "Avis d'imposition 2023", type: "Impôts", icon: "📊" },
        { title: "3 bulletins de salaire", type: "Emploi", icon: "💼" },
        { title: "Attestation employeur", type: "Travail", icon: "📄" },
        { title: "Relevé de compte", type: "Banque", icon: "🏦" }
      ]
    }
  },
  {
    query: "Mon contrat de travail",
    result: {
      title: "Contrat CDI",
      type: "Emploi",
      detail: "Entreprise X — Signé le 15/03/2022",
      icon: "📄"
    }
  },
  {
    query: "Dossier inscription école",
    isMultiDoc: true,
    result: {
      collectionName: "Inscription 2024",
      documents: [
        { title: "Justificatif domicile", type: "Logement", icon: "🏠" },
        { title: "Livret de famille", type: "Identité", icon: "👨‍👩‍👧" },
        { title: "Carnet de vaccination", type: "Santé", icon: "💉" }
      ]
    }
  },
  {
    query: "Attestation assurance habitation",
    result: {
      title: "Attestation MMA",
      type: "Assurance",
      detail: "Valide jusqu'au 31/12/2024",
      icon: "🏠",
      expiresAt: "soon"
    }
  }
];

export const HeroSearchDemo = () => {
  const [currentQueryIndex, setCurrentQueryIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [isTyping, setIsTyping] = useState(true);
  const [showNotification, setShowNotification] = useState(false);

  const currentDemo = demoQueries[currentQueryIndex];

  // Check if current result has expiration
  const hasExpiration = !currentDemo.isMultiDoc && (currentDemo.result as SearchResult).expiresAt;

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (isTyping) {
      // Typing effect
      if (displayedText.length < currentDemo.query.length) {
        timeout = setTimeout(() => {
          setDisplayedText(currentDemo.query.slice(0, displayedText.length + 1));
        }, 50 + Math.random() * 30); // Variable typing speed for realism
      } else {
        // Finished typing, show result after a short delay
        timeout = setTimeout(() => {
          setShowResult(true);
          setIsTyping(false);
        }, 300);
      }
    } else {
      // Wait, then move to next query
      timeout = setTimeout(() => {
        setShowResult(false);
        setShowNotification(false);
        setDisplayedText("");
        setCurrentQueryIndex((prev) => (prev + 1) % demoQueries.length);
        setIsTyping(true);
      }, 4000);
    }

    return () => clearTimeout(timeout);
  }, [displayedText, isTyping, currentDemo.query]);

  // Show notification toast after result appears (only for expiring documents)
  useEffect(() => {
    if (showResult && hasExpiration) {
      const timeout = setTimeout(() => {
        setShowNotification(true);
      }, 800);
      return () => clearTimeout(timeout);
    }
  }, [showResult, hasExpiration]);

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/20 via-primary/10 to-transparent rounded-3xl blur-2xl -z-10 scale-105" />
      
      {/* Glass container */}
      <div className="backdrop-blur-2xl bg-white/70 border border-white/30 rounded-2xl shadow-2xl shadow-primary/10 overflow-hidden">
        {/* Search input area */}
        <div className="p-4 sm:p-6">
          <div className="flex items-center gap-3 backdrop-blur-xl bg-white/80 border border-white/40 rounded-xl px-4 py-3.5 shadow-lg shadow-black/5">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex-1 min-h-[24px] relative">
              <span className="text-foreground font-medium">
                {displayedText}
              </span>
              {isTyping && (
                <motion.span
                  className="inline-block w-0.5 h-5 bg-primary ml-0.5 align-middle"
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                />
              )}
            </div>
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/30 flex-shrink-0">
              <Search className="w-4 h-4 text-primary-foreground" />
            </div>
          </div>
        </div>

        {/* Result area */}
        <div className="px-4 sm:px-6 pb-4 sm:pb-6 min-h-[88px]">
          <AnimatePresence mode="wait">
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              >
                <Link to="/auth?mode=signup" className="block">
                  {currentDemo.isMultiDoc ? (
                    // Multi-document result (Collector)
                    <div className="backdrop-blur-xl bg-white/60 border border-white/30 rounded-xl p-4 hover:bg-white/80 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <FolderOpen className="w-3 h-3" />
                          Dossier créé
                        </span>
                        <span className="text-xs font-semibold text-foreground">
                          {currentDemo.result.collectionName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 overflow-x-auto pb-1">
                        {currentDemo.result.documents.map((doc, i) => (
                          <div 
                            key={i}
                            className="flex-shrink-0 flex items-center gap-2 bg-white/80 rounded-lg px-3 py-2 border border-white/50"
                          >
                            <span className="text-lg">{doc.icon}</span>
                            <div className="min-w-0">
                              <p className="text-xs font-medium truncate max-w-[100px]">{doc.title}</p>
                              <p className="text-[10px] text-muted-foreground">{doc.type}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    // Single document result
                    (() => {
                      const singleResult = currentDemo.result as SearchResult;
                      return (
                        <div className="flex items-center gap-4 backdrop-blur-xl bg-white/60 border border-white/30 rounded-xl p-4 hover:bg-white/80 hover:shadow-xl hover:shadow-primary/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer group">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center text-2xl shadow-sm group-hover:scale-105 transition-transform">
                            {singleResult.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                                {singleResult.type}
                              </span>
                              <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                98% match
                              </span>
                              {singleResult.expiresAt && (
                                <motion.span 
                                  className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full flex items-center gap-1"
                                  initial={{ opacity: 0, scale: 0.8 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: 0.3 }}
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  Expire bientôt
                                </motion.span>
                              )}
                            </div>
                            <p className="font-semibold text-sm sm:text-base truncate">{singleResult.title}</p>
                            <p className="text-xs sm:text-sm text-muted-foreground truncate">{singleResult.detail}</p>
                          </div>
                          <FileText className="w-5 h-5 text-muted-foreground/50 flex-shrink-0 group-hover:text-primary transition-colors" />
                        </div>
                      );
                    })()
                  )}
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Notification Toast */}
      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, x: 50, y: 0 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ type: "spring", damping: 20, stiffness: 300 }}
            className="absolute -right-4 sm:right-0 top-4 z-20"
          >
            <div className="bg-white rounded-xl shadow-2xl shadow-black/20 border border-border/50 p-3 pr-4 flex items-start gap-3 max-w-[260px]">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <motion.div
                  animate={{ rotate: [0, -15, 15, -10, 10, 0] }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Bell className="w-4 h-4 text-amber-600" />
                </motion.div>
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-foreground">Rappel automatique</p>
                <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
                  Votre attestation MMA expire dans 30 jours. Pensez à la renouveler !
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subtle hint */}
      <motion.p 
        className="text-center text-xs text-muted-foreground mt-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        Posez une question, trouvez votre document
      </motion.p>
    </div>
  );
};
